import { Injectable, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
    FrontdoorExtensionAvailabilityService,
    FrontdoorSelectionService,
} from '@netfoundry-ui/shared/frontdoor-selection';
import { ApiService, FrontdoorV3, hasNetworkExtension, NETWORK_EXTENSIONS } from '@netfoundry-ui/shared/services';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';
import { AssistantScope, assistantScopeKey } from './api/assistant-scope';

/** The console area a URL belongs to, as far as the assistant cares. */
export type AssistantArea = 'fd' | 'frontdoor' | 'network';

/**
 * `/fd/...` is the v3 Frontdoor sub-app and `/frontdoor/...` the v1/v2 one. Everything else talks to the current
 * network.
 */
export function assistantAreaOf(url: string): AssistantArea {
    const path = url.split(/[?#;]/)[0];
    if (path === '/fd' || path.startsWith('/fd/')) {
        return 'fd';
    }
    if (path === '/frontdoor' || path.startsWith('/frontdoor/')) {
        return 'frontdoor';
    }
    return 'network';
}

/**
 * The scope the assistant talks to for the page on screen, or null when it has none. In `/fd` that is the selected
 * Frontdoor account and in `/frontdoor` nothing, because the assistant only resolves v3 accounts. Everywhere else it is
 * the current network. A scope is only offered when its network carries the AI_ASSISTANT extension.
 */
@Injectable({ providedIn: 'root' })
export class AssistantScopeService {
    private readonly _scope = signal<AssistantScope | null>(null, { equal: (a, b) => keyOf(a) === keyOf(b) });
    readonly scope = this._scope.asReadonly();

    constructor(
        router: Router,
        apiService: ApiService,
        selection: FrontdoorSelectionService,
        frontdoorExtensions: FrontdoorExtensionAvailabilityService
    ) {
        // Before the first navigation ends, router.url is '/', which would offer a network scope on an /fd page for a
        // moment.
        const initialUrl = router.navigated ? [router.url] : [];
        const area$ = router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            map((event) => event.urlAfterRedirects),
            startWith(...initialUrl),
            map(assistantAreaOf),
            distinctUntilChanged()
        );
        combineLatest([
            area$,
            apiService.currentNetwork,
            selection.current$,
            frontdoorExtensions.enabled$(NETWORK_EXTENSIONS.AI_ASSISTANT),
        ]).subscribe(([area, network, frontdoor, frontdoorAssistant]) =>
            this._scope.set(resolveScope(area, network, frontdoor, frontdoorAssistant))
        );
    }
}

function resolveScope(
    area: AssistantArea,
    network: any,
    frontdoor: FrontdoorV3 | null,
    frontdoorAssistant: boolean
): AssistantScope | null {
    if (area === 'frontdoor') {
        return null;
    }
    if (area === 'fd') {
        // A placeholder entry carries no id, and enabled$ stays false until the account's network has answered.
        return frontdoor?.id && frontdoorAssistant ? { kind: 'frontdoors', id: frontdoor.id } : null;
    }
    return network?.id && hasNetworkExtension(network, NETWORK_EXTENSIONS.AI_ASSISTANT)
        ? { kind: 'networks', id: network.id }
        : null;
}

function keyOf(scope: AssistantScope | null): string {
    return scope ? assistantScopeKey(scope) : '';
}
